import { useState, useCallback, useRef, useEffect } from "react";
import styles from "./HistoryItem.module.css";
import { DefaultButton, TextField } from "@fluentui/react";
import { Delete24Regular, Edit24Regular, Checkmark24Regular, Dismiss24Regular } from "@fluentui/react-icons";
export interface HistoryData {
    id: string;
    title: string;
    timestamp: number;
    conversation_type?: string;
}
interface HistoryItemProps {
    item: HistoryData;
    onSelect: (item: HistoryData) => void;
    onDelete: (id: string) => void;
    onRename: (id: string, title: string) => void;
}
export function HistoryItem({ item, onSelect, onDelete, onRename }: HistoryItemProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameTitle, setRenameTitle] = useState(item.title);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isRenaming) {
            setRenameTitle(item.title);
        }
    }, [isRenaming, item.title]);

    useEffect(() => {
        if (isRenaming && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isRenaming]);

    const handleDelete = useCallback(() => {
        setIsModalOpen(false);
        onDelete(item.id);
    }, [item.id, onDelete]);

    const handleRename = useCallback(() => {
        if (renameTitle.trim() && renameTitle !== item.title) {
            onRename(item.id, renameTitle);
        }
        setIsRenaming(false);
    }, [item.id, item.title, renameTitle, onRename]);

    const handleCancelRename = useCallback(() => {
        setIsRenaming(false);
        setRenameTitle(item.title);
    }, [item.title]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleRename();
        } else if (e.key === "Escape") {
            handleCancelRename();
        }
    };

    return (
        <div className={styles.historyItem}>
            {isRenaming ? (
                <div className={styles.renameContainer}>
                    <input
                        ref={inputRef}
                        className={styles.renameInput}
                        value={renameTitle}
                        onChange={e => setRenameTitle(e.target.value)}
                        onKeyDown={onKeyDown}
                        onBlur={handleRename}
                    />
                    <div className={styles.renameActions}>
                        <button onClick={handleRename} className={styles.actionButton} aria-label="confirm rename">
                            <Checkmark24Regular className={styles.actionIcon} />
                        </button>
                        <button onClick={handleCancelRename} className={styles.actionButton} aria-label="cancel rename">
                            <Dismiss24Regular className={styles.actionIcon} />
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <button onClick={() => onSelect(item)} className={styles.historyItemButton}>
                        <div className={styles.historyItemTitle} title={item.title}>
                            {item.title}
                        </div>
                    </button>
                    <div className={styles.itemActions}>
                        <button onClick={() => setIsRenaming(true)} className={styles.actionButton} aria-label="rename this chat history">
                            <Edit24Regular className={styles.actionIcon} />
                        </button>
                        <button onClick={() => setIsModalOpen(true)} className={styles.actionButton} aria-label="delete this chat history">
                            <Delete24Regular className={styles.actionIcon} />
                        </button>
                    </div>
                </>
            )}
            <DeleteHistoryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={handleDelete} />
        </div>
    );
}
function DeleteHistoryModal({ isOpen, onClose, onConfirm }: { isOpen: boolean; onClose: () => void; onConfirm: () => void }) {
    if (!isOpen) return null;
    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h2 className={styles.modalTitle}>Delete Chat Session</h2>
                <p className={styles.modalDescription}>Are you sure you want to delete this chat?</p>
                <div className={styles.modalActions}>
                    <DefaultButton onClick={onClose} className={styles.modalCancelButton}>
                        Cancel
                    </DefaultButton>
                    <DefaultButton onClick={onConfirm} className={styles.modalConfirmButton}>
                        Delete
                    </DefaultButton>
                </div>
            </div>
        </div>
    );
}
